import DOMPurify from 'dompurify';
import { useTranslation } from '@frontend/i18n';
import { Text, TextLink } from '@frontend/design-system';
import { useOnlineBillPayAssets } from '../../hooks/use-onlinebillpay-assets';
import { CopyButton } from '../Shared/copy-button';
import { IconWithText } from '../Shared/icon-with-text';
import { Snippet, snippetStyle } from '../Shared/snippet';
import * as styles from './onlinebillpay-settings.styles';

const fieldNames = {
  url: 'onlineBillPayUrl',
  qrCode: 'qrCode',
  button: 'button',
} as const;

export const OnlineBillPaySettings = () => {
  const { t } = useTranslation('payments');
  const { onlineBillPayUrl, onlineBillPayBtnText, onlineBillPayBtnHtml, onlineBillPayQRHtml, onlineBillPayQRImg } =
    useOnlineBillPayAssets();

  const urlSectionId = `${fieldNames.url}-section`;
  const qRCodeSectionId = `${fieldNames.qrCode}-section`;
  const btnSectionId = `${fieldNames.button}-section`;

  return (
    <>
      {onlineBillPayUrl && (
        <section>
          <Text css={styles.pageRowTitle} id={urlSectionId} as='h2'>
            {t(`Your location's URL`)}
          </Text>
          <div css={[styles.pageUrlRow]}>
            <Snippet>{onlineBillPayUrl}</Snippet>
            <CopyButton
              trackingId='pay-portal-onlinebillpay-btn-copylink'
              copyLabel='Copy link'
              copyText={onlineBillPayUrl}
            />
          </div>
        </section>
      )}
      <hr css={styles.pageHr} />
      {onlineBillPayQRHtml && (
        <section>
          <Text css={styles.pageRowTitle} id={qRCodeSectionId} as='h2'>
            {t(`Your location's QR Code`)}
          </Text>
          <div css={styles.pageQRRow}>
            <div
              css={styles.imageBorder}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(onlineBillPayQRHtml, { ADD_ATTR: ['target'] }) }}
            ></div>
            <div css={styles.column}>
              <TextLink href={onlineBillPayQRImg} download={true} css={styles.textDecorationNone}>
                <IconWithText trackingId='pay-portal-onlinebillpay-btn-saveimage' iconName='image'>
                  Save image
                </IconWithText>
              </TextLink>
              <CopyButton
                trackingId='pay-portal-onlinebillpay-btn-copyqrembed'
                copyLabel={t('Copy Embed Code')}
                copyText={onlineBillPayQRHtml}
              />
            </div>
          </div>
        </section>
      )}
      <hr css={styles.pageHr} />
      {onlineBillPayUrl && (
        <section>
          <Text css={styles.pageRowTitle} id={btnSectionId} as='h2'>
            {t(`Button snippet`)}
          </Text>
          <div css={styles.pageRow}>
            <Text size='medium' css={styles.marginBottom}>
              {t(`Use this code to put a "{{onlineBillPayBtnText}}" button on your website.`, { onlineBillPayBtnText })}
            </Text>
            <div css={styles.row}>
              <div
                css={snippetStyle}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(onlineBillPayBtnHtml, { ADD_ATTR: ['target'] }) }}
              />
              <CopyButton
                trackingId='pay-portal-onlinebillpay-btn-copycodeembed'
                copyLabel={t('Copy Embed Code')}
                copyText={onlineBillPayBtnHtml}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
};
